import { Children, cloneElement, isValidElement, type ReactNode } from "react";
import type { ServerProviderSkill } from "@afar-research/contracts";

import { formatProviderSkillDisplayName } from "../../providerSkillPresentation";
import {
  HIGHLIGHTED_INLINE_CHIP_CLASS_NAME,
  CHAT_INLINE_CHIP_LABEL_CLASS_NAME,
  COMPOSER_INLINE_CHIP_ICON_CLASS_NAME,
  SKILL_CHIP_ICON_SVG,
} from "../composerInlineChip";
import { matchConversationCommand } from "./conversationCommands.logic";

const SKILL_TOKEN_REGEX = /(^|\s)\$([a-zA-Z][a-zA-Z0-9:_-]*)(?=\s|$)/g;

type InlineSkill = Pick<ServerProviderSkill, "name" | "displayName">;
type InlineMarkdownElementProps = {
  children?: ReactNode;
  node?: { tagName?: string };
};

export function SkillInlineText(props: {
  text: string;
  skills: ReadonlyArray<InlineSkill>;
  renderUnmatchedSkills?: boolean;
}) {
  const nodes: ReactNode[] = [];
  let cursor = 0;
  function appendText(text: string) {
    let remainder = text;
    let match = matchConversationCommand(remainder);
    while (match) {
      nodes.push(remainder.slice(0, match.start));
      const command = remainder.slice(match.start, match.end);
      nodes.push(
        <span
          key={`command:${nodes.length}`}
          className={HIGHLIGHTED_INLINE_CHIP_CLASS_NAME}
          data-conversation-command={command}
          data-markdown-copy={command}
        >
          {command}
        </span>,
      );
      remainder = remainder.slice(match.end);
      match = matchConversationCommand(remainder);
    }
    nodes.push(remainder);
  }

  for (const match of props.text.matchAll(SKILL_TOKEN_REGEX)) {
    const prefix = match[1] ?? "";
    const name = match[2] ?? "";
    const start = (match.index ?? 0) + prefix.length;
    const rawText = `$${name}`;
    const skill =
      props.skills.find((candidate) => candidate.name === name) ??
      (props.renderUnmatchedSkills ? { name } : undefined);
    if (!skill) {
      continue;
    }

    if (start > cursor) {
      appendText(props.text.slice(cursor, start));
    }
    nodes.push(<SkillChip key={`${start}:${name}`} skill={skill} copyText={rawText} />);
    cursor = start + rawText.length;
  }

  if (cursor < props.text.length) {
    appendText(props.text.slice(cursor));
  }
  return <>{nodes}</>;
}

export function renderSkillInlineMarkdownChildren(
  children: ReactNode,
  skills: ReadonlyArray<InlineSkill>,
  renderUnmatchedSkills = false,
): ReactNode {
  return Children.map(children, (child) => {
    if (typeof child === "string") {
      return (
        <SkillInlineText
          text={child}
          skills={skills}
          renderUnmatchedSkills={renderUnmatchedSkills}
        />
      );
    }
    if (!isValidElement<InlineMarkdownElementProps>(child)) {
      return child;
    }
    const tagName = typeof child.type === "string" ? child.type : child.props.node?.tagName;
    if (tagName === "code" || tagName === "a") {
      return child;
    }
    if (!("children" in child.props)) {
      return child;
    }
    return cloneElement(
      child,
      undefined,
      renderSkillInlineMarkdownChildren(child.props.children, skills, renderUnmatchedSkills),
    );
  });
}

function SkillChip(props: { skill: InlineSkill; copyText?: string }) {
  const chip = (
    <span className={HIGHLIGHTED_INLINE_CHIP_CLASS_NAME}>
      <span
        aria-hidden="true"
        className={COMPOSER_INLINE_CHIP_ICON_CLASS_NAME}
        dangerouslySetInnerHTML={{ __html: SKILL_CHIP_ICON_SVG }}
      />
      <span className={CHAT_INLINE_CHIP_LABEL_CLASS_NAME}>
        {formatProviderSkillDisplayName(props.skill)}
      </span>
    </span>
  );
  if (props.copyText === undefined) {
    return chip;
  }
  return (
    <span className="inline-flex align-middle leading-none" data-markdown-copy={props.copyText}>
      {chip}
    </span>
  );
}
